import { BlindKingsGame, type LastAction } from '@blind-kings/game-core';
import { api } from 'aws-blocks';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAuthContext } from '../../../contexts/AuthContext';
import { useSnackBarContext } from '../../../contexts/SnackBarContext';
import useRoomSubscription from '../../../hooks/use-room-subscription';
import { playerIsInGameId } from '../../../utils/active-games';
import { needsLiveGamePoll } from '../../../utils/live-sync';

export type GameData = Record<string, unknown> & {
  encodedGameState?: string;
  deleted?: boolean;
  error?: string;
  message?: string;
  gameId?: string;
  status?: string;
  gameMode?: string;
  lastFocus?: {
    id: string;
    from: { x: number; y: number };
    to: { x: number; y: number };
  };
  lastAction?: LastAction;
  updatedAt?: number;
  unchanged?: boolean;
  firstPlayer?: { playerId?: string };
  secondPlayer?: { playerId?: string };
  openSeat?: boolean;
};

function mergeGame(prev: GameData | undefined, message: GameData): GameData {
  if (message.unchanged) {
    return prev ?? message;
  }
  if (
    prev &&
    typeof prev.updatedAt === 'number' &&
    typeof message.updatedAt === 'number' &&
    message.updatedAt < prev.updatedAt
  ) {
    return prev;
  }
  const next = { ...prev, ...message };
  if (next.encodedGameState) {
    return {
      ...next,
      ...BlindKingsGame.decodeGameState(String(next.encodedGameState)),
    };
  }
  return next;
}

function authErrorKind(err: unknown): 'auth' | 'forbidden' | 'other' {
  const msg = err instanceof Error ? err.message : String(err ?? '');
  const lower = msg.toLowerCase();
  if (
    lower.includes('not authenticated') ||
    lower.includes('please log in') ||
    lower.includes('unauthorized') ||
    lower.includes('sign in')
  ) {
    return 'auth';
  }
  if (
    lower.includes('not in this game') ||
    lower.includes('forbidden') ||
    lower.includes('access denied')
  ) {
    return 'forbidden';
  }
  return 'other';
}

export default function useGameRouter() {
  const { gameId } = useParams();
  const [game, setGame] = useState<GameData | undefined>();
  const [finishedInitialQuery, setFinishedInitialQuery] = useState(false);
  const { addSnackBar } = useSnackBarContext();
  const { currentUser, userData } = useAuthContext();
  const updatedAtRef = useRef<number | undefined>(undefined);

  const playerId = userData?.playerId;
  const isSeated =
    !!gameId &&
    (playerIsInGameId(userData, gameId) ||
      game?.firstPlayer?.playerId === playerId ||
      game?.secondPlayer?.playerId === playerId);

  useEffect(() => {
    if (!gameId) return;
    let cancelled = false;
    setGame(undefined);
    setFinishedInitialQuery(false);
    updatedAtRef.current = undefined;

    (async () => {
      if (!currentUser) {
        if (!cancelled) {
          setGame({
            error: 'auth',
            message: 'Sign in to view this game',
          });
          setFinishedInitialQuery(true);
        }
        return;
      }

      try {
        const initial = (await api.getGame(gameId)) as GameData | null;
        if (cancelled) return;
        if (!initial || initial.unchanged) {
          setGame({ error: 'not_found', message: 'Game not found' });
          setFinishedInitialQuery(true);
          return;
        }
        updatedAtRef.current =
          typeof initial.updatedAt === 'number' ? initial.updatedAt : undefined;
        setGame(mergeGame(undefined, initial));
        setFinishedInitialQuery(true);
      } catch (err) {
        if (cancelled) return;
        const kind = authErrorKind(err);
        if (kind === 'auth') {
          setGame({
            error: 'auth',
            message: 'Sign in to view this game',
          });
        } else if (kind === 'forbidden') {
          setGame({
            error: 'forbidden',
            message: 'You are not a player in this game',
          });
        } else {
          addSnackBar({
            title: 'ERROR',
            message: err instanceof Error ? err.message : 'Failed to load game',
            variant: 'danger',
            timeout: 3000,
          });
          setGame({
            error: 'not_found',
            message: err instanceof Error ? err.message : 'Game not found',
          });
        }
        setFinishedInitialQuery(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [gameId, addSnackBar, currentUser]);

  const onEvent = useCallback((payload: GameData) => {
    if (payload.unchanged) return;
    if (typeof payload.updatedAt === 'number') {
      updatedAtRef.current = payload.updatedAt;
    }
    setGame((prev) => mergeGame(prev, payload));
  }, []);

  const applyGameUpdate = useCallback((payload: GameData) => {
    if (payload.unchanged) return;
    if (typeof payload.updatedAt === 'number') {
      updatedAtRef.current = payload.updatedAt;
    }
    setGame((prev) => mergeGame(prev, payload));
  }, []);

  const fetchSnapshot = useCallback(async () => {
    if (!gameId) return null;
    const snapshot = (await api.getGame(
      gameId,
      updatedAtRef.current
    )) as GameData | null;
    if (snapshot && !snapshot.unchanged && typeof snapshot.updatedAt === 'number') {
      updatedAtRef.current = snapshot.updatedAt;
    }
    return snapshot;
  }, [gameId]);

  useRoomSubscription<GameData>({
    roomId: gameId,
    enabled:
      finishedInitialQuery &&
      !!game?.gameId &&
      !game?.error &&
      !!currentUser &&
      isSeated &&
      needsLiveGamePoll(game.gameMode, game.status),
    onEvent,
    fetchSnapshot,
  });

  return { gameId, game, finishedInitialQuery, applyGameUpdate };
}
